import type { HelpdeskAuthorType, IHelpdeskAttachment, IHelpdeskMessage } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import MarkedReact from "marked-react";
import React, { useMemo } from "react";
import { SAFE_MARKDOWN_RENDERER } from "../../management/board/safe-markdown";
import KuikaAvatar, { getIdentitySeed } from "../../ui-kit/kuika-avatar/kuika-avatar";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { formatFileSize } from "./helpdesk-files";
import { authorInitials, buildThread, HelpdeskThreadItem, isCustomerSide, PENDING_MESSAGE_ID } from "./helpdesk-thread";
import { formatTicketDate, formatTicketTime } from "./helpdesk-tickets";

export interface HelpdeskMessageThreadProps {
  messages: IHelpdeskMessage[];
  /** The reply on its way, drawn after the thread (and grouped with it) until the real one lands. */
  pendingMessage?: IHelpdeskMessage | null;
  /** What the pending reply is doing ("Sending…", upload progress); empty once it is sent. */
  pendingStatus?: string;
  onOpenAttachment: (attachment: IHelpdeskAttachment) => void;
}

interface IMessageAvatarProps {
  message: IHelpdeskMessage;
}

// The role badge next to the author; an empty label shows no badge. Support messages show only the
// badge, never the agent's name.
const AUTHOR_LABELS: Record<HelpdeskAuthorType, string> = {
  requester: "",
  colleague: "Colleague",
  support: "Kuika Support"
};

const AVATAR_SIZE: number = 28;

/** Decorative: the author line beside it names the person. */
const MessageAvatar = ({ message }: IMessageAvatarProps): React.ReactElement => {
  if (message.authorType === "support") {
    return (
      <KuikaAvatar size={AVATAR_SIZE} aria-hidden="true">
        <KuikaSvg name="kuika-logo-inside" width={14} height={14} color="var(--accent-fg)" />
      </KuikaAvatar>
    );
  }
  return (
    <KuikaAvatar size={AVATAR_SIZE} seed={getIdentitySeed({ fullName: message.authorName })} aria-hidden="true">
      {authorInitials(message.authorName)}
    </KuikaAvatar>
  );
};

/**
 * A ticket's conversation as a chat: the customer's side on the right, Kuika Support on the left, a
 * line between days, and one author's quick follow-ups grouped under a single header.
 */
const HelpdeskMessageThread = ({
  messages,
  pendingMessage = null,
  pendingStatus = "",
  onOpenAttachment
}: HelpdeskMessageThreadProps): React.ReactElement => {
  const items: HelpdeskThreadItem[] = useMemo(
    (): HelpdeskThreadItem[] => buildThread(pendingMessage ? [...messages, pendingMessage] : messages),
    [messages, pendingMessage]
  );

  return (
    <ol className="kuika-helpdesk-thread">
      {items.map((item: HelpdeskThreadItem): React.ReactElement => {
        if (item.kind === "day") {
          return (
            <li key={`day-${item.key}`} className="kuika-helpdesk-thread__day">
              <span>{item.label}</span>
            </li>
          );
        }

        const message: IHelpdeskMessage = item.message;
        const hasBubble: boolean = Boolean(message.body) || message.attachments.length > 0;
        const isPending: boolean = message.id === PENDING_MESSAGE_ID;
        return (
          <li
            key={message.id}
            className={classNames(
              "kuika-helpdesk-message",
              `kuika-helpdesk-message--${message.authorType}`,
              isCustomerSide(message.authorType)
                ? "kuika-helpdesk-message--outgoing"
                : "kuika-helpdesk-message--incoming",
              {
                "kuika-helpdesk-message--continued": item.continued,
                "kuika-helpdesk-message--sending": isPending && pendingStatus.length > 0
              }
            )}
            data-testid={isPending ? "helpdeskPendingReply" : undefined}
          >
            <div className="kuika-helpdesk-message__avatar">
              {!item.continued && <MessageAvatar message={message} />}
            </div>
            <div className="kuika-helpdesk-message__content">
              {!item.continued && (
                <div className="kuika-helpdesk-message__meta">
                  <span data-testid="profileName" className="kuika-helpdesk-message__author">
                    {message.authorType !== "support" && message.authorName}
                    {AUTHOR_LABELS[message.authorType] && (
                      <span className="kuika-helpdesk-message__role">{AUTHOR_LABELS[message.authorType]}</span>
                    )}
                  </span>
                  {!isPending && (
                    <time
                      data-testid="ticketMessageDate"
                      className="kuika-helpdesk-message__time"
                      dateTime={message.createdAt}
                      title={formatTicketDate(message.createdAt)}
                    >
                      {formatTicketTime(message.createdAt)}
                    </time>
                  )}
                </div>
              )}
              {hasBubble && (
                <div className="kuika-helpdesk-message__bubble">
                  {message.body && (
                    <div data-testid="ticketMessage" className="kuika-helpdesk-message__body">
                      <MarkedReact gfm breaks value={message.body} renderer={SAFE_MARKDOWN_RENDERER} />
                    </div>
                  )}
                  {message.attachments.length > 0 && (
                    <ul className="kuika-helpdesk-message__attachments">
                      {message.attachments.map((attachment: IHelpdeskAttachment): React.ReactElement => (
                        <li key={attachment.id}>
                          <button
                            type="button"
                            data-testid="ticketAttachment"
                            className="kuika-helpdesk-message__attachment"
                            disabled={attachment.status !== "available"}
                            title={attachment.status === "available" ? attachment.fileName : "Processing…"}
                            onClick={(): void => onOpenAttachment(attachment)}
                          >
                            <KuikaSvg name={attachment.isImage ? "scan-eye" : "file"} width={14} height={14} />
                            <span className="kuika-helpdesk-message__attachment-name">{attachment.fileName}</span>
                            <span className="kuika-helpdesk-message__attachment-size">
                              {formatFileSize(attachment.sizeBytes)}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {isPending && pendingStatus && (
                <span role="status" className="kuika-helpdesk-message__status">
                  {pendingStatus}
                </span>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default HelpdeskMessageThread;
